// `grid` — many lines at once (docs/plans/transaction-wizard.md §6.5): the
// line form's rows filtered by `row_filter`, its `columns` shown and its
// `editable` subset typed into with the SHARED cell editor (the same widget,
// mask and look-up the transaction's own grid uses). Each cell edit fires the
// column's item_change; the edited rows are completed (row_complete) on Next.
// `row_tools` run with that row as the current line.
//
// Spreadsheet gestures over the EDITABLE cells (the read-only columns are
// shown, never typed into or pasted over):
//   - arrows / Tab / Enter move between them (the shared useGridKeyboardNav);
//   - a block pasted from a spreadsheet lands from the focused cell, row by
//     row, adding lines only when the step allows it (`allow_add`);
//   - Fill down (Ctrl+D, or the button) copies the focused cell into the rows
//     selected below it (Shift+click / Shift+↓), else into every row after it.
// Every value a paste or a fill writes goes through the typing path, cell by
// cell in order (`ctl.setCells`): the value, then that column's item_change.
//
// The plan of what a paste / fill writes is pure (../grid.ts).

import { useMemo, useRef, useState } from 'react'
import type { ClipboardEvent as ReactClipboardEvent, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { ArrowDownToLine, Plus, Trash2 } from 'lucide-react'
import { InlineCellEditor, coerceCellText } from '../../txn/list/InlineCellEditor'
import { useGridKeyboardNav } from '../../txn/list/useGridKeyboardNav'
import { TxnRuntimeProvider } from '../../txn/context'
import { isKey, isProtect } from '../../txn/utils'
import { columnOf, headingOf, keyOf, readCol, scopeOf } from '../model'
import { gridRows } from '../machine'
import { fitColumnWidths, isMultiCellText, parseClipboardGrid, planFillDown, planPaste, type GridWritePlan } from '../grid'
import { T, button, muted, table, td, th } from '../styles'
import { tw } from '../../testHooks'
import type { GridCellSet } from '../useWizard'
import type { StepProps } from './common'
import { display } from './common'

/** The focused editable cell: a position in the visible rows and in the
 *  editable columns. */
interface Cell { pos: number; col: number }

export default function GridStep({ ctl, handheld }: StepProps) {
  const { step, fi, frame, base, screen, model, host, ui, run } = ctl
  const boxRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState<Cell | null>(null)
  // The other end of a row range in the active column (Shift+click / ↓).
  const [rangeEnd, setRangeEnd] = useState<number | null>(null)
  useGridKeyboardNav(boxRef, { enabled: true })

  const form = step && fi && model ? String(step.form_no || model.line_form || fi.lineForm) : ''
  const columns = useMemo(() => (step?.columns?.length ? step.columns : (step?.editable || [])), [step])
  const editableKeys = useMemo(
    () => new Set((step?.editable || []).map(c => (fi ? keyOf(fi, form, c) : c))), [step, fi, form])
  const rows = useMemo(() => frame?.record.details[form] || [], [frame, form])
  const indexes = useMemo(() => (step && fi && frame && model && run
    ? gridRows(model, fi, step, frame,
      (probe) => scopeOf(probe, fi, { step, run: { run_id: run.run_id }, user: host.user }))
    : []), [model, fi, step, frame, run, host.user])
  // Editable columns in display order — what the keyboard, a paste and a
  // fill address.
  const editCols = useMemo(() => (fi ? columns.filter(c => columnOf(fi, form, c) && editableKeys.has(keyOf(fi, form, c))) : []),
    [columns, fi, form, editableKeys])
  const widths = useMemo(() => (fi ? fitColumnWidths(
    columns.map(c => ({ key: c, heading: headingOf(fi, form, c), editable: editCols.includes(c) })),
    (c) => indexes.map(i => display(readCol(rows[i], fi, form, c))),
  ) : {}), [columns, fi, form, editCols, indexes, rows])

  if (!step || !fi || !frame || !base || !screen || !model || !run) return null
  const visibleTools = (step.row_tools || []).filter(t => ctl.toolVisible(t))
  const busy = !!ctl.busy

  /** A cell a person could not type into either (the row's protect, a
   *  protected column, a saved line's key). */
  const locked = (line: number, column: string): boolean => {
    const row = rows[line]
    const col = columnOf(fi, form, column)
    if (!row || !col) return true
    const p = (row._protect as Record<string, 0 | 1> | undefined)?.[keyOf(fi, form, column)]
    if (p === 1) return true
    if (p === 0) return false
    return isProtect(col) || (!!row._txn_id && isKey(col))
  }

  const apply = async (plan: GridWritePlan, fromClipboard: boolean) => {
    const cells: GridCellSet[] = plan.writes.map(w => {
      const col = columnOf(fi, form, w.column)
      return {
        line: w.line, column: w.column,
        // Clipboard text is coerced as the cell editor coerces typing.
        value: fromClipboard && col ? coerceCellText(col, String(w.value ?? '')) : w.value,
      }
    })
    if (plan.dropped) {
      host.notify('info', `${plan.dropped} value${plan.dropped === 1 ? ' was' : 's were'} not ${fromClipboard ? 'pasted' : 'filled'} — `
        + (fromClipboard && !step.allow_add ? 'no more lines or editable columns, or locked cells.' : 'locked cells or no more editable columns.'))
    }
    await ctl.setCells(cells, plan.addRows)
  }

  const onPaste = (e: ReactClipboardEvent) => {
    const text = e.clipboardData?.getData('text/plain') ?? ''
    // A single value pastes natively into the focused input.
    if (!active || !isMultiCellText(text) || busy) return
    e.preventDefault()
    void apply(planPaste({
      data: parseClipboardGrid(text), rows: indexes, columns: editCols,
      startRow: active.pos, startCol: active.col, allowAdd: !!step.allow_add, locked,
    }), true)
  }

  const fillDown = () => {
    if (!active || busy) return
    const column = editCols[active.col]
    if (!column) return
    const from = rangeEnd != null ? Math.min(active.pos, rangeEnd) : active.pos
    const to = rangeEnd != null ? Math.max(active.pos, rangeEnd) : null
    const src = rows[indexes[from]]
    if (!src || (to != null && to === from)) return
    void apply(planFillDown({
      rows: indexes, column, fromRow: from, toRow: to,
      value: src[keyOf(fi, form, column)], locked,
    }), false)
  }

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'd') {
      e.preventDefault()
      fillDown()
      return
    }
    if (e.shiftKey && !e.ctrlKey && !e.metaKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && active) {
      e.preventDefault()
      const cur = rangeEnd ?? active.pos
      const next = Math.max(0, Math.min(indexes.length - 1, cur + (e.key === 'ArrowDown' ? 1 : -1)))
      setRangeEnd(next === active.pos ? null : next)
    }
  }

  const inRange = (pos: number, col: number) => !!active && rangeEnd != null && col === active.col
    && pos >= Math.min(active.pos, rangeEnd) && pos <= Math.max(active.pos, rangeEnd)

  return (
    <TxnRuntimeProvider value={screen.txn}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} {...tw('form', form, { child: true })}>
        {editCols.length > 0 && indexes.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" disabled={!active || busy} onClick={fillDown}
              title="Copy the focused cell into the selected rows below it (Shift+click / Shift+↓), or every row after it (Ctrl+D)"
              style={{ ...button(false, false, !active || busy), minHeight: handheld ? 40 : 30, fontSize: 13, padding: '0 10px' }}
              {...tw('action', 'fill_down')}>
              <ArrowDownToLine size={14} /> Fill down
            </button>
            {!handheld && (
              <span style={{ ...muted, fontSize: 13 }}>
                Paste rows from a spreadsheet into the focused cell · Ctrl+D fills down · arrows, Tab and Enter move
              </span>
            )}
          </div>
        )}
        <div ref={boxRef} onPaste={onPaste} onKeyDown={onKeyDown}
          style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 8 }}>
          <table style={{ ...table, width: 'auto', minWidth: '100%' }}>
            <colgroup>
              {columns.map(c => <col key={c} style={{ width: `${widths[c] || 10}ch` }} />)}
              {(visibleTools.length > 0 || step.allow_delete) && <col />}
            </colgroup>
            <thead>
              <tr>
                {columns.map(c => <th key={c} style={th}>{headingOf(fi, form, c)}</th>)}
                {(visibleTools.length > 0 || step.allow_delete) && <th style={th} />}
              </tr>
            </thead>
            <tbody>
              {indexes.map((i, pos) => {
                const row = rows[i]
                const errs = ui.gridErrors[i] || {}
                const issueFor = (c: string) => ctl.issues.find(x => x.type === 'E' && x.row === i
                  && String(x.form_no) === form && x.field && keyOf(fi, form, x.field) === keyOf(fi, form, c))?.message
                return (
                  <tr key={String(row._localId || row._txn_id || i)} {...tw('row', i)}
                    style={{ background: frame.line_index === i && frame.line_form === form ? 'var(--color-accent-soft, #eef4ff)' : undefined }}>
                    {columns.map(c => {
                      const col = columnOf(fi, form, c)
                      const key = keyOf(fi, form, c)
                      const err = errs[c] || issueFor(c)
                      const ci = editCols.indexOf(c)
                      if (col && ci >= 0) {
                        const protect = (row._protect as Record<string, 0 | 1> | undefined)?.[key]
                        const selected = inRange(pos, ci)
                        return (
                          <td key={c}
                            style={{
                              ...td, padding: '3px 4px',
                              background: selected ? 'rgba(59,130,246,0.10)' : undefined,
                              boxShadow: active && active.pos === pos && active.col === ci
                                ? 'inset 0 0 0 1px rgba(59,130,246,0.65)' : undefined,
                            }}
                            data-grid-cell={`${i}::${key}`} data-grid-row={String(i)} data-grid-col={key}
                            onFocus={() => {
                              if (!active || active.pos !== pos || active.col !== ci) setRangeEnd(null)
                              setActive({ pos, col: ci })
                            }}
                            onMouseDown={(e) => {
                              // Shift+click in the same column selects the rows between.
                              if (!e.shiftKey || !active || active.col !== ci) return
                              e.preventDefault()
                              setRangeEnd(pos === active.pos ? null : pos)
                            }}
                            {...tw('field', key, { error: !!err })}>
                            <InlineCellEditor
                              project={host.project}
                              objName={base.obj_name}
                              col={protect === 0 ? { ...col, protect: false } : col}
                              value={row[key]}
                              error={err}
                              readOnlyKey={!!row._txn_id}
                              viewOnly={protect === 1}
                              onChange={(v) => ctl.change(`line${form}.${key}`, v, i)}
                              rowValues={row}
                              headerValues={frame.record.header}
                              uploadDocument={screen.txn.uploadDocument}
                            />
                            {err && <div style={{ color: '#a61b1b', fontSize: 13, marginTop: 2 }}>{err}</div>}
                          </td>
                        )
                      }
                      return (
                        <td key={c} style={{ ...td, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={display(readCol(row, fi, form, c))}>
                          {display(readCol(row, fi, form, c))}
                          {err && <div style={{ color: '#a61b1b', fontSize: 13, whiteSpace: 'normal' }}>{err}</div>}
                        </td>
                      )
                    })}
                    {(visibleTools.length > 0 || step.allow_delete) && (
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          {visibleTools.map(t => (
                            <button key={t.label} type="button" style={{ ...button(false), minHeight: handheld ? 40 : 28, fontSize: 13, padding: '0 8px' }}
                              onClick={() => void ctl.runTool(t, i)} {...tw('wizard-tool', t.label)}>{t.label}</button>
                          ))}
                          {step.allow_delete && (
                            <button type="button" title="Remove line" aria-label="Remove line"
                              style={{ ...button(false), minHeight: handheld ? 40 : 28, padding: '0 8px' }}
                              onClick={() => ctl.deleteGridLine(i)} {...tw('action', 'delete_line')}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
              {!indexes.length && (
                <tr><td colSpan={columns.length + 1} style={{ ...td, ...muted, textAlign: 'center' }}>No lines.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {step.allow_add && (
          <div>
            <button type="button" style={button(handheld)} onClick={() => void ctl.addGridLine()} {...tw('action', 'add_line')}>
              <Plus size={16} /> Add line
            </button>
          </div>
        )}
      </div>
    </TxnRuntimeProvider>
  )
}
